import { useIsReadOnly } from '@/lib/casl'
import { truncateFieldValueToCharacterLimit } from '@/lib/field-character-limits'
import { useFieldContext } from '@/lib/form-contexts'
import { cn } from '@/lib/utils'
import { Label } from '../ui/label'
import { Textarea, type TextareaProps } from '../ui/textarea'
import FieldInfo from './FieldInfo'
import { Typography } from '@/components/ui/typography'
import type { ReactNode } from 'react'

export type TextareaInputProps = TextareaProps & {
  label?: ReactNode
  type?: string
  labelEnd?: ReactNode
  readOnlyEmptyText?: ReactNode
  transformValue?: ((value: string) => string) | null
  characterMaxLength?: number | null
}

interface TextareaInputControlProps extends TextareaInputProps {
  fieldName: string
  fieldValue: string | null | undefined
  onFieldChange: (value: string) => void
  onFieldBlur?: () => void
  fieldInfo?: ReactNode
}

export function TextareaInputControl({
  fieldName,
  fieldValue,
  onFieldChange,
  onFieldBlur = () => {},
  fieldInfo,
  id,
  label,
  type,
  labelEnd,
  placeholder,
  className,
  readOnlyEmptyText = 'No description provided',
  transformValue = null,
  characterMaxLength = null,
  onChange,
  ...props
}: TextareaInputControlProps) {
  const { isReadOnly } = useIsReadOnly()

  return (
    <div className="flex flex-col gap-y-2 w-full">
      {!!label && (
        <div className="inline-flex items-center">
          <Label htmlFor={id || fieldName}>{label}</Label>
          {labelEnd}
        </div>
      )}

      {isReadOnly ? (
        <Typography variant="supporting" tone={!fieldValue ? 'muted' : 'default'}>
          {fieldValue || readOnlyEmptyText}
        </Typography>
      ) : (
        <Textarea
          id={id || fieldName}
          placeholder={placeholder}
          value={fieldValue ?? ''}
          onChange={(e) => {
            const transformedValue =
              typeof transformValue === 'function' ? transformValue(e.target.value) : e.target.value
            const nextValue = truncateFieldValueToCharacterLimit(
              transformedValue,
              characterMaxLength
            )

            onFieldChange(nextValue)
            onChange?.(e)
          }}
          onBlur={onFieldBlur}
          className={cn(props.rows ? 'field-sizing-fixed' : null, className)}
          {...props}
        />
      )}

      {type !== 'hidden' && fieldInfo}
    </div>
  )
}

export default function TextareaInput(props: TextareaInputProps) {
  const field = useFieldContext<string | null | undefined>()
  return (
    <TextareaInputControl
      {...props}
      fieldName={field.name}
      fieldValue={field.state.value}
      onFieldChange={field.handleChange}
      onFieldBlur={field.handleBlur}
      fieldInfo={<FieldInfo />}
    />
  )
}
